import { createTheme } from '@mui/material/styles';
import { REGISTER_THEMES } from './registerBranding';

const focusRing = {
  outline: '3px solid rgba(31, 111, 178, 0.28)',
  outlineOffset: 2,
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1f6fb2',
      dark: '#155487',
      light: '#e8f2fb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#746a00',
      dark: '#554e00',
      light: '#f7efad',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2f7d4a',
      dark: '#225f37',
      light: '#eaf5ed',
    },
    warning: {
      main: '#b85e12',
      dark: '#8c4408',
      light: '#fff3e5',
    },
    error: {
      main: '#c43e49',
      dark: '#992d37',
      light: '#fdecef',
    },
    info: {
      main: '#1477a9',
      dark: '#0e597f',
      light: '#e6f4fa',
    },
    background: {
      default: '#f4f7fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#182230',
      secondary: '#5f6c7b',
      disabled: '#8b96a5',
    },
    divider: '#dbe2ea',
    action: {
      hover: 'rgba(24, 34, 48, 0.045)',
      selected: 'rgba(31, 111, 178, 0.10)',
      disabled: '#98a2b3',
      disabledBackground: '#e8ecf1',
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h1: { fontWeight: 800, letterSpacing: '-0.025em' },
    h2: { fontWeight: 800, letterSpacing: '-0.025em' },
    h3: { fontWeight: 800, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.018em' },
    h5: { fontWeight: 700, letterSpacing: '-0.012em' },
    h6: { fontWeight: 700, letterSpacing: '-0.008em' },
    subtitle1: { fontWeight: 600 },
    button: { fontWeight: 700, letterSpacing: '0.005em' },
    caption: { letterSpacing: '0.012em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: '#f4f7fa',
          textRendering: 'optimizeLegibility',
        },
        body: {
          color: '#182230',
          backgroundColor: '#f4f7fa',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '::selection': {
          color: '#102033',
          backgroundColor: 'rgba(241, 210, 18, 0.46)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: 8,
          paddingInline: 14,
          textTransform: 'none',
          transition:
            'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
          '&:focus-visible': focusRing,
        },
        contained: {
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.10)',
          '&:hover': {
            boxShadow: '0 5px 14px rgba(15, 23, 42, 0.13)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          backgroundColor: 'rgba(255, 255, 255, 0.78)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus-visible': focusRing,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: '#dbe2ea',
          backgroundImage: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: '1px solid rgba(24, 34, 48, 0.08)',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.20)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 12px',
          fontWeight: 700,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: 8,
          padding: '12px 24px 20px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#ffffff',
          transition: 'box-shadow 160ms ease, background-color 160ms ease',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#8ea0b3',
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(31, 111, 178, 0.12)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
          },
        },
        notchedOutline: {
          borderColor: '#cbd5df',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#5f6c7b',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 2,
          marginRight: 2,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 7,
          backgroundColor: '#111827',
          fontSize: 12,
          lineHeight: 1.45,
          padding: '7px 10px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          textTransform: 'none',
          '&:focus-visible': focusRing,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 6,
          marginInline: 6,
          marginBlock: 2,
          '&:focus-visible': focusRing,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderColor: '#dbe2ea',
          color: '#263445',
          backgroundColor: '#ffffff',
          '& .MuiDataGrid-columnHeaders': {
            color: '#273445',
            backgroundColor: '#f7f9fc',
            borderBottomColor: '#dbe2ea',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
          '& .MuiDataGrid-cell': {
            borderBottomColor: '#e7ecf1',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f7fafc',
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'rgba(31, 111, 178, 0.09)',
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: 'rgba(31, 111, 178, 0.13)',
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
            outline: '2px solid rgba(31, 111, 178, 0.42)',
            outlineOffset: -2,
          },
          '& .MuiDataGrid-footerContainer': {
            borderTopColor: '#dbe2ea',
            backgroundColor: '#fbfcfd',
          },
          '& .MuiDataGrid-toolbarContainer': {
            minHeight: 52,
            gap: 4,
            padding: '7px 9px',
            borderBottom: '1px solid #e2e8ef',
          },
          '& .MuiDataGrid-overlay': {
            color: '#5f6c7b',
          },
        },
      },
    },
  },
});

// Brand accents are separate from the semantic safety/status palette.
const createRegisterTheme = (brand) => createTheme(theme, {
  palette: {
    primary: { main: brand.primary, dark: brand.dark, light: brand.soft, contrastText: '#ffffff' },
    secondary: { main: '#25262B', dark: '#17181C', light: '#ECEDEF', contrastText: '#ffffff' },
    background: { default: brand.background, paper: '#ffffff' },
    action: { selected: `rgba(${brand.rgb}, 0.08)` },
  },
  components: {
    MuiCssBaseline: { styleOverrides: {
      html: { backgroundColor: brand.background },
      body: { backgroundColor: brand.background },
      '::selection': { color: brand.dark, backgroundColor: brand.soft },
    } },
    MuiButton: { styleOverrides: { root: {
      '&:focus-visible': { outline: `3px solid rgba(${brand.rgb}, 0.28)`, outlineOffset: 2 },
    } } },
    MuiIconButton: { styleOverrides: { root: {
      '&:focus-visible': { outline: `3px solid rgba(${brand.rgb}, 0.28)`, outlineOffset: 2 },
    } } },
    MuiTab: { styleOverrides: { root: {
      '&:focus-visible': { outline: `3px solid rgba(${brand.rgb}, 0.28)`, outlineOffset: 2 },
    } } },
    MuiMenuItem: { styleOverrides: { root: {
      '&:focus-visible': { outline: `3px solid rgba(${brand.rgb}, 0.28)`, outlineOffset: 2 },
    } } },
    MuiOutlinedInput: { styleOverrides: { root: {
      '&.Mui-focused': { boxShadow: `0 0 0 3px rgba(${brand.rgb}, 0.12)` },
    } } },
    MuiDataGrid: { styleOverrides: { root: {
      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `rgba(${brand.rgb}, 0.07)` },
      '& .MuiDataGrid-row.Mui-selected:hover': { backgroundColor: `rgba(${brand.rgb}, 0.11)` },
      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
        outline: `2px solid rgba(${brand.rgb}, 0.42)`, outlineOffset: -2,
      },
    } } },
  },
});

export const registerThemes = Object.fromEntries(REGISTER_THEMES
  .filter((brand) => brand.key !== 'default')
  .map((brand) => [brand.key, createRegisterTheme(brand)]));
export const orlenTheme = registerThemes.orlen;
export const bumiArmadaTheme = registerThemes['bumi-armada'];

export default theme;
